import TextField, { TextFieldProps } from "@mui/material/TextField";
import { getCharacterCounter } from "core/model/utils/strings";
import { GREY_100, GREY_600 } from "ds_legacy/materials/colors";
import { padding, sizing } from "ds_legacy/materials/metrics";
import { FONT_SIZE_14 } from "ds_legacy/materials/typography";
import React from "react";
import { FormElement, FormElementProps, isValid } from "react-forms-state";
import { useTranslations } from "translations";
import { getHelperText } from "../Validation";

export type TextAreaProps = Pick<
  TextFieldProps,
  | "disabled"
  | "fullWidth"
  | "label"
  | "maxRows"
  | "minRows"
  | "multiline"
  | "onKeyPress"
  | "placeholder"
  | "required"
  | "rows"
  | "id"
  | "sx"
  | "type"
  | "value"
  | "helperText"
  | "error"
> & {
  ariaLabel?: string;
  characterLimit?: number;
  elementName?: string;
  endAdornment?: React.ReactNode;
  hasCustomValidation?: boolean;
  hideError?: boolean;
  margin?: "dense" | "none" | "normal";
  marginOverride?: string;
  maxWidth?: string;
  onChange?: (value: any, modelPath?: string) => void;
  paddingArea?: string;
  startAdornment?: React.ReactNode;
  withFlatModel?: boolean;
  validation?: any;
  width?: string;
};

export default function TextArea({
  ariaLabel,
  characterLimit,
  disabled,
  elementName,
  endAdornment,
  error,
  fullWidth,
  hasCustomValidation,
  helperText,
  hideError = false,
  id,
  label,
  margin,
  marginOverride,
  maxRows,
  maxWidth,
  minRows,
  multiline = true,
  onChange,
  onKeyPress,
  placeholder,
  required,
  rows,
  startAdornment,
  sx,
  type,
  validation,
  value,
  width,
}: TextAreaProps) {
  const translations = useTranslations();

  const customHelperText = getHelperText({
    hasCustomValidation: hasCustomValidation ?? true,
    translations,
    validation,
  });

  const characterLimitCounter = getCharacterCounter({
    characterLimit,
    translations,
    value,
  });

  return (
    <TextField
      disabled={disabled}
      error={hideError ? false : error ?? !isValid(validation)}
      FormHelperTextProps={{
        sx: {
          maxWidth: "inherit",
          wordWrap: "break-word",
        },
      }}
      fullWidth={fullWidth}
      helperText={
        (hideError ? undefined : customHelperText) ??
        helperText ??
        characterLimitCounter
      }
      id={id}
      InputLabelProps={{
        style: {
          background: "#fff",
          marginRight: sizing(3),
          paddingRight: sizing(0.5),
        },
      }}
      InputProps={{
        endAdornment,
        startAdornment,
        style: {
          background: disabled ? GREY_100 : "#fff",
          fontSize: FONT_SIZE_14,
        },
      }}
      inputProps={{
        "aria-label": ariaLabel,
        "data-testid": elementName,
        maxLength: characterLimit,
        style: { padding: padding(1.5) },
      }}
      label={label}
      margin={margin || "normal"}
      maxRows={maxRows}
      minRows={minRows}
      multiline={multiline}
      name={elementName}
      placeholder={placeholder}
      required={required}
      rows={rows}
      sx={{
        margin: marginOverride || undefined,
        maxWidth: maxWidth || "auto",
        width: fullWidth ? "100%" : width || "auto",
        ...sx,
        "& .Mui-disabled textarea": {
          WebkitTextFillColor: GREY_600,
        },
        "& .MuiInputBase-root": {
          padding: padding(0.5),
        },
        "& textarea:first-of-type::placeholder": {
          color: GREY_600,
          opacity: 1,
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        },
      }}
      type={type}
      value={value === null ? "" : value}
      variant="outlined"
      onChange={(e) => onChange?.(e.target.value)}
      onKeyPress={onKeyPress}
    />
  );
}

export const ConnectedTextarea = FormElement()<TextAreaProps & FormElementProps>(
  TextArea,
);
